import * as React from 'react';
import { ReactElement } from 'react';
import { List, ListSubheader, Divider, styled } from '@mui/material';
import { useTranslate } from 'react-admin';

import { SearchHistoryPanelProps } from './types';
import { SearchHistoryContextProvider } from './SearchHistoryContext';
import { SearchHistoryItem } from './SearchHistoryItem';
import { useArrowKeysToNavigate } from './useArrowKeysToNavigate';

/**
 * A component which displays search history inside a MUI [`<List>`](https://mui.com/components/lists/),
 *
 * @param props {SearchHistoryPanelProps}
 * @param props.history {string[]} A list of previous searches.
 * @param props.onSelect {Function} The function to call when the item is selected.
 * @param props.children {ReactElement} A ReactElement which will be cloned for each search history item.
 */
export const SearchHistoryPanel = (
    props: SearchHistoryPanelProps
): ReactElement => {
    const listRef = React.useRef<HTMLUListElement>(null);
    const {
        children = <SearchHistoryItem />,
        history,
        onSelect,
        ...rest
    } = props;
    useArrowKeysToNavigate(listRef);

    const translate = useTranslate();

    return (
        // @ts-ignore
        <Root component="nav" dense ref={listRef} {...rest}>
            <ListSubheader sx={{ bgcolor: 'inherit' }}>
                <strong>
                    {translate('ra-search.recent', { _: 'Recent' })}
                </strong>
                <Divider />
            </ListSubheader>
            {history.map((item, index) => (
                <SearchHistoryContextProvider
                    key={index}
                    value={{ onSelect, item }}
                >
                    {children}
                </SearchHistoryContextProvider>
            ))}
        </Root>
    );
};

const PREFIX = 'RaSearchHistoryPanel';

const Root = styled(List, {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})({
    maxHeight: 'calc(100vh - 100px)',
    minWidth: 216,
});
